// ---------------------------------------------------------------------------
// stat-card — headline totals with an inline sparkline trend
// ---------------------------------------------------------------------------

import { createElement } from '../../../core/dom/helpers.js';
import { sparkline } from '../../../core/charts/sparkline.js';
import { numberFormat } from '../../../core/charts/svg-util.js';
import { isAbortError } from '../../../core/http/is-abort-error.js';

/**
 * @typedef {import('../../../contracts/card-grid.js').CardDescriptor} CardDescriptor
 * @typedef {import('../../../contracts/dashboard.js').DashboardCardContext} CardContext
 * @typedef {import('../../../contracts/dashboard.js').StatsResponse} StatsResponse
 * @typedef {import('../../../contracts/card-grid.js').CardHandle} CardHandle
 * @typedef {CardDescriptor & { render: (ctx: CardContext) => CardHandle }} DashboardCard
 */

/**
 * @typedef {Object} StatCardConfig
 * @property {string} id
 * @property {string} title
 * @property {string} [trendKey] - key in `stats.trends` to draw as a sparkline.
 */

/**
 * @param {StatCardConfig} config
 * @returns {DashboardCard}
 */
export function createStatCard(config) {
  return {
    id: config.id,
    title: config.title,
    render(ctx) {
      return mountStatCard(config, ctx);
    },
  };
}

/**
 * @param {StatCardConfig} config
 * @param {CardContext} ctx
 * @returns {{ el: HTMLElement, destroy: () => void }}
 */
function mountStatCard(config, ctx) {
  const el = createElement('div', { class: 'dashboard-stat' });
  let destroyed = false;
  let seq = 0;

  /** @param {string} text */
  function renderMessage(text) {
    el.replaceChildren(createElement('p', { class: 'dashboard-stat__message' }, [text]));
  }

  /** @param {StatsResponse} stats */
  function render(stats) {
    const totals = Array.isArray(stats.totals) ? stats.totals : [];
    const figures = totals.map((t) =>
      createElement('div', { class: 'dashboard-stat__figure' }, [
        createElement('span', { class: 'dashboard-stat__value' }, [numberFormat(Number(t.count) || 0)]),
        createElement('span', { class: 'dashboard-stat__label' }, [String(t.label ?? '')]),
      ]),
    );

    /** @type {(string|Node)[]} */
    const children = [createElement('div', { class: 'dashboard-stat__figures' }, figures)];

    const trend = config.trendKey && stats.trends ? stats.trends[config.trendKey] : undefined;
    if (Array.isArray(trend) && trend.length) {
      const values = trend.map((p) => Number(p.value) || 0);
      const media = createElement('div', {
        class: 'dashboard-stat__spark',
        role: 'img',
        'aria-label': `${config.title} trend`,
      });
      // Trusted SVG template; all dynamic tokens escaped inside sparkline().
      media.innerHTML = sparkline(values, { idPrefix: `spark-${config.id}`, title: `${config.title} trend` });
      children.push(media);
    }

    el.replaceChildren(...children);
  }

  function load() {
    const mine = ++seq;
    el.setAttribute('aria-busy', 'true');
    ctx.apiClient.get('/dashboard/stats', { signal: ctx.signal })
      .then((/** @type {unknown} */ raw) => {
        if (destroyed || mine !== seq) return;
        el.setAttribute('aria-busy', 'false');
        render(/** @type {StatsResponse} */ (raw) || /** @type {any} */ ({}));
      })
      .catch((/** @type {unknown} */ err) => {
        if (isAbortError(err) || destroyed || mine !== seq) return;
        el.setAttribute('aria-busy', 'false');
        ctx.logger.error('Dashboard stats load failed', { data: { card: config.id } });
        renderMessage('Couldn’t load stats.');
      });
  }

  renderMessage('Loading…');
  load();

  return {
    el,
    destroy() {
      destroyed = true;
    },
  };
}
